import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {
  Borders,
  Buffer,
  Cell,
  Column,
  Style,
  Workbook,
  Worksheet
} from 'exceljs';
import * as FileSaver from 'file-saver';
/** Browser-safe replacement for the `filenamify` Node package. */
function sanitizeFilename(name: string, replacement = ''): string {
  return (
    name
      // Remove characters illegal in Windows/POSIX filenames
      .replace(/[<>:"/\\|?*\x00-\x1F]/g, replacement)
      // Remove leading dots and trailing spaces/dots
      .replace(/^\.+/, '')
      .trim() || 'data'
  );
}
import {lastValueFrom} from 'rxjs';
import {map} from 'rxjs/operators';

import {CockpitCardTableExportProps} from '../../../cockpit/cockpit-shared/cockpit/cockpit-services/cockpit-export/cockpit-export-prop';
import {HtmlTableParserService} from './html-table-parser.service';
import {TableDataExportUtilsService} from './table-data-export-utils.service';
import {
  Table,
  TableCellProps,
  TableCellStyle,
  TableImage
} from './table-properties';
import {workbook} from '../../../exceljs';
import {FilenameService} from '../filename.service';

const EXCEL_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

const ROW_HEIGHT = 20;

@Injectable({
  providedIn: 'root'
})
export class TableExportService {
  // eslint-disable-next-line @typescript-eslint/max-params
  constructor(
    private readonly translate: TranslateService,
    private readonly filenameService: FilenameService,
    private readonly tableParser: HtmlTableParserService,
    private readonly utils: TableDataExportUtilsService
  ) {}

  /**
   * Exports a singular HTML table element from the DOM to an excel file.
   * @param tableId The HTML "id" attribute of the table to export as excel.
   * @param filenameKey The ngx-translate key for the filename.
   * @param headerKey The ngx-translate key for the header row to insert above the table.
   */
  async exportHtmlTable(tableId: string, filenameKey = '', headerKey = '') {
    this.clearWorkbook();
    const wb = await this._exportHtmlTables([headerKey], tableId);
    await this.saveWorkbookAsExcel(wb, filenameKey);
  }

  async exportHtmlTableInElement(
    element: HTMLElement,
    filenameKey = '',
    headerKey = '',
    filterById?: string
  ) {
    const wb = await this._exportHtmlTablesFromElement(
      element,
      [headerKey],
      filterById
    );
    await this.saveWorkbookAsExcel(wb, filenameKey);
  }

  /**
   * Exports all HTML table elements from the current DOM to a single worksheet of an excel file.
   * @param filenameKey The ngx-translate key for the filename of the Excel document.
   * @param headerKeys An Array of keys that will be insert above the tables in order of appearance (First key goes above first table and so on).
   */
  async exportAllHtmlTablesInDom(filenameKey = '', headerKeys: string[] = []) {
    const wb = await this._exportHtmlTables(headerKeys);
    await this.saveWorkbookAsExcel(wb, filenameKey);
  }

  async exportCockpitCardTables(
    exportData: CockpitCardTableExportProps[],
    expertMode = false
  ) {
    this.clearWorkbook();
    for (const exportCard of exportData) {
      const worksheet = this.appendFilledWorksheetToWorkbook(
        workbook,
        exportCard.tables,
        {
          name: await this.getFilename(exportCard.cardTitleKey),
          useNameAsHeader: true,
          tableHeaders: await this.getTableHeaders(exportCard.tableTitleKeys),
          striped: true
        }
      );

      if (exportCard.images?.length > 0) {
        this.addImagesToWorksheet(worksheet, exportCard.images);
      }
    }
    await this.saveWorkbookAsExcel(
      workbook,
      expertMode
        ? this.filenameService.getFilename('common.filenames.cockpit-expert')
        : this.filenameService.getFilename('common.filenames.cockpit')
    );
  }

  /**
   * Exports a matrix of TableCellProps to an Excel file.
   * @param matrix The matrix to export to Excel.
   * @param filenameKey The ngx-translate key for the filename of the Excel document.
   * @param headerKey An Array of keys that will be insert above the tables in order of appearance (First key goes above first table and so on).
   */
  async exportMatrix(
    matrix: TableCellProps[][],
    filenameKey,
    zebra?: boolean,
    headerKey: string[] = [],
    columns?: Partial<Column>[]
  ) {
    workbook.eachSheet((sheet, id) => {
      workbook.removeWorksheet(id);
    });
    const wb = this.createAndFillWorkbook([matrix], headerKey, zebra, columns);
    await this.saveWorkbookAsExcel(wb, filenameKey);
  }

  private async saveWorkbookAsExcel(wb: Workbook, filenameKey: string) {
    const buffer = await wb.xlsx.writeBuffer({useStyles: true});
    this.writeBuffer(buffer, await this.getFilename(filenameKey));
  }

  private async _exportHtmlTablesFromElement(
    element: HTMLElement,
    headerKeys: string[] = [],
    filterById: string = null
  ): Promise<Workbook> {
    const headers: string[] = await this.getTableHeaders(headerKeys);

    return this.createAndFillWorkbook(
      this.tableParser.parseAllTables(element, filterById),
      headers
    );
  }

  private async _exportHtmlTables(
    headerKeys: string[] = [],
    filterById: string = null
  ): Promise<Workbook> {
    return this._exportHtmlTablesFromElement(
      document.body,
      headerKeys,
      filterById
    );
  }

  private createAndFillWorkbook(
    tables: Table[],
    tableHeaders: string[],
    striped?: boolean,
    columns?: Partial<Column>[]
  ) {
    this.clearWorkbook();

    this.appendFilledWorksheetToWorkbook(
      workbook,
      tables,
      {
        tableHeaders,
        striped
      },
      columns
    );
    return workbook;
  }

  private appendFilledWorksheetToWorkbook(
    wb: Workbook,
    tables: Table[],
    options: WorksheetOptions,
    columns?: Partial<Column>[]
  ) {
    const worksheet = wb.addWorksheet(options?.name);
    this.prepareWorksheetColumns(worksheet, this.utils.getColumnCount(tables));

    if (options?.useNameAsHeader && options?.name) {
      this.insertHeaderRow(worksheet, options.name, true);
    }

    for (let index = 0; index < tables.length; index++) {
      const matrix = tables[index];
      this.appendMatrixToWorksheet(
        worksheet,
        matrix,
        options?.tableHeaders[index],
        options?.striped
      );
    }

    if (columns) {
      worksheet.columns = columns;
    }
    return worksheet;
  }

  private appendMatrixToWorksheet(
    worksheet: Worksheet,
    matrix: TableCellProps[][],
    header?: string,
    striped?: boolean
  ) {
    // Header einfügen wenn vorhanden
    this.insertHeaderRow(worksheet, header);

    // Tabelle einfügen
    const matrixValues = matrix.map(row => row.map(cell => cell.value));
    const tableRows = worksheet.addRows(matrixValues);

    tableRows.forEach((row, rowIndex) => {
      row.eachCell((cell, colIndex) => {
        const matrixCell = matrix[rowIndex][colIndex - 1];
        const cellStyle = matrixCell.styles;
        this.applyCellStyles(cell, cellStyle, rowIndex, striped);
      });
    });

    // Zellen mergen
    const mergableCells = this.utils.findAllSpanningCells(matrix);
    for (const cell of mergableCells) {
      const topLeftCellRow = tableRows[0].number + cell.rowIndex;
      const topLeftCellCol = cell.colIndex + 1;
      const bottomRightCellRow = topLeftCellRow + cell.rowspan - 1;
      const bottomRightCellCol = topLeftCellCol + cell.colspan - 1;
      try {
        worksheet.mergeCells(
          topLeftCellRow,
          topLeftCellCol,
          bottomRightCellRow,
          bottomRightCellCol
        );
      } catch (e: unknown) {
        console.error(
          e,
          matrix,
          cell,
          topLeftCellRow,
          topLeftCellCol,
          bottomRightCellRow,
          bottomRightCellCol
        );
      }
    }
    // Leere Zeile für Layout
    worksheet.addRow([]);
  }

  private insertHeaderRow(
    worksheet: Worksheet,
    header: string,
    addEmptyBelow?: boolean
  ) {
    if (header && header.length > 0) {
      const headerRow = worksheet.addRow([header]);
      headerRow.font = {
        bold: true
      };

      if (addEmptyBelow) {
        worksheet.addRow([]);
      }
    }
  }

  private addImagesToWorksheet(worksheet: Worksheet, images: TableImage[]) {
    for (const [i, image] of images.entries()) {
      const id = worksheet.workbook.addImage({
        base64: image.imgData,
        extension: 'png'
      });
      // Place image in col 0, there is no other content in the worksheet
      const col =
        worksheet.actualColumnCount <= 0 ? 0 : worksheet.actualColumnCount + 2;

      // Place image below the previous image
      const row = i <= 0 ? 1 : images[i - 1].height / ROW_HEIGHT + 2;

      worksheet.addImage(id, {
        tl: {col, row},
        ext: {width: image.width, height: image.height},
        editAs: 'absolute'
      });
    }
  }

  // eslint-disable-next-line max-lines-per-function
  private applyCellStyles(
    cell: Cell,
    cellStyles: TableCellStyle[],
    rowIndex: number,
    striped = true
  ) {
    this.applyDefaults(cell);
    if (cellStyles && cellStyles.length > 0) {
      for (const style of cellStyles) {
        switch (style) {
          case TableCellStyle.Header:
            cell.style = Object.assign(cell.style, {
              fill: {
                type: 'pattern',
                pattern: 'solid',
                fgColor: {argb: 'FFD3D3D3'}
              }
            } as Partial<Style>);
            break;
          case TableCellStyle.Centered:
            cell.style = Object.assign(cell.style, {
              alignment: {
                horizontal: 'center',
                vertical: 'middle',
                wrapText: true
              }
            } as Partial<Style>);
            break;
          case TableCellStyle.Bold:
            cell.style = Object.assign(cell.style, {
              font: {
                bold: true
              }
            } as Partial<Style>);
            break;
          case TableCellStyle.Body:
            cell.style = Object.assign(cell.style, {
              alignment: {
                vertical: 'top',
                horizontal: 'left',
                wrapText: true
              }
            } as Partial<Style>);
            break;
          case TableCellStyle.Hint:
            cell.style = Object.assign(cell.style, {
              alignment: {vertical: 'top', horizontal: 'left', wrapText: true},
              font: {italic: true, size: 10},
              border: {bottom: null, left: null, right: null}
            } as Partial<Style>);
            cell.worksheet.getRow(Number(cell.row)).height = 75;
            break;
          default:
            console.error('Unexpected Cell Style');
        }
      }
    } else if (striped) {
      // Gerade Zeilen grau hinterlegen
      if (!(cell.value as any).richText) {
        this.applyAlternatingGrayBackground(rowIndex, cell);
      }
    }
  }

  private applyDefaults(cell: Cell) {
    const cellDefaultBorders: Partial<Borders> = {
      top: {style: 'thin'},
      left: {style: 'thin'},
      bottom: {style: 'thin'},
      right: {style: 'thin'}
    };
    cell.border = Object.assign(cellDefaultBorders, cell.border);
    cell.style = Object.assign(cell.style, {
      alignment: {
        wrapText: true
      }
    } as Partial<Style>);
  }

  private applyAlternatingGrayBackground(rowIndex: number, cell: Cell) {
    if ((rowIndex + 1) % 2 === 0) {
      const cellStyleFillGray: Partial<Style> = {
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: {argb: 'FFEEEEEE'}
        }
      };

      // Abwechselnde Farbe wird von anderen Zelleigenschaften überschrieben
      cell.style = Object.assign(cellStyleFillGray, cell.style);
    }
  }

  private async getTableHeaders(headerKeys: string[]) {
    let headers: string[] = [];
    if (headerKeys?.length > 0) {
      headers = await lastValueFrom(
        this.translate
          .get(headerKeys)
          .pipe(map((obj: ArrayLike<string>) => Object.values<string>(obj)))
      );
    }
    return headers;
  }

  private async getFilename(filenameKey: string) {
    let filename = 'data';
    if (filenameKey.trim().length > 0) {
      filename = sanitizeFilename(
        await lastValueFrom(this.translate.get(filenameKey))
      );
    }
    return filename;
  }

  private prepareWorksheetColumns(
    worksheet: Worksheet,
    longestColumnLength: number
  ) {
    const columns: Column[] = [];
    for (let i = 0; i < longestColumnLength; i++) {
      columns.push({
        key: `value${i}`,
        width: 30
      } as Partial<Column> as Column);
    }
    worksheet.columns = columns;
  }

  private writeBuffer(excelBuffer: Buffer, filename: string) {
    const data: Blob = new Blob([excelBuffer], {type: EXCEL_TYPE});
    FileSaver.saveAs(data, `${filename}.xlsx`);
  }

  private isObject(object) {
    return object !== null && typeof object === 'object';
  }

  private clearWorkbook() {
    workbook.eachSheet((sheet, id) => {
      workbook.removeWorksheet(id);
    });
  }
}

interface WorksheetOptions {
  /**
   * The name of the worksheet. Will be truncated if necessary.
   */
  name?: string;

  /**
   * Defines if the worksheet name should be put at the top of the worksheet as a header.
   */
  useNameAsHeader?: boolean;

  /**
   * An array of headers to put before each table in a worksheet in sequence.
   */
  tableHeaders?: string[];

  /**
   * Defines if the table should have alternating background colors.
   */
  striped?: boolean;
}
